// CityPageGenerator.js - Builds /guides/<country>/<city>/index.html, one page per city.
// Works for every city automatically from the generated guides; a content file in src/data/cityPages
// adds the hand-written parts (themes, card texts, areas, plan, history). Styles and behaviour live in
// public/city-page.css and public/city-page.js, shared by all city pages.
import { SITE_BASE, escapeHtml as e } from './guidesSite';
import { IOS_URL, GOOGLE_TAG, stores, player as playButton, shorten } from './pageParts';
import { researchSections } from './cityResearch';

// With this many tours or more, the guides lead the page (a guide hub); with fewer, the planning sections do
// and the guides get a "Hear the stories" strip; with none, the page is planning only.
export const MANY_GUIDES = 5;
const LANG_ORDER = ['en', 'pt', 'es', 'fr', 'de', 'it', 'nl', 'pl', 'bg', 'uk', 'ru', 'zh', 'ja', 'ar'];
const LANG_NAMES = {
  en: 'English', pt: 'Portuguese', es: 'Spanish', fr: 'French', de: 'German', it: 'Italian', nl: 'Dutch',
  pl: 'Polish', bg: 'Bulgarian', uk: 'Ukrainian', ru: 'Russian', zh: 'Chinese', ja: 'Japanese', ar: 'Arabic',
};

export const baseLang = (code) => String(code || 'en').toLowerCase().split(/[-_]/)[0];
const langName = (l) => LANG_NAMES[l] || l.toUpperCase();
const langRank = (l) => { const i = LANG_ORDER.indexOf(l); return i === -1 ? 99 : i; };
const monthYear = () => new Date().toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });

/**
 * Translations of the same tour share their stops. Without a content file, guides with the same number of
 * stops and the same first stop location are treated as language versions of one tour.
 */
const fingerprint = (g) => {
  if (!g.firstStop || g.firstStop.lat === undefined || g.firstStop.lat === null || g.firstStop.lat === '') return `slug:${g.slug}`;
  const r = (n) => Number(n).toFixed(3);
  return `${g.stops}|${r(g.firstStop.lat)},${r(g.firstStop.lng)}`;
};

const makeGroup = (key, versions, extra = {}) => {
  const langs = Object.keys(versions).sort((a, b) => langRank(a) - langRank(b));
  const primaryLang = langs.includes('en') ? 'en' : langs[0];
  const primary = versions[primaryLang];
  // a sample that really plays: the primary language first, then the others
  const withSample = [primaryLang, ...langs.filter(l => l !== primaryLang)].map(l => versions[l]).find(v => v.sample);
  return {
    key, versions, langs, primaryLang, primary,
    title: extra.title || primary.name,
    blurb: extra.blurb || shorten(primary.description),
    theme: extra.theme || 'more',
    partner: extra.partner || null,
    stops: primary.stops,
    thumb: primary.thumbnail,
    sample: withSample ? { url: withSample.sample, lang: baseLang(withSample.language), stop: withSample.firstStopName } : null,
  };
};

/** Groups the city's guides into tours (with their language versions), using the content file when there is one. */
export const groupGuides = (entries, content = {}) => {
  const bySlug = {};
  entries.forEach(g => { bySlug[g.slug] = g; });
  const used = new Set();
  const groups = [];

  (content.guides || []).forEach(def => {
    const versions = {};
    Object.values(def.versions || {}).forEach(slug => {
      // keyed by the guide's real language: an English guide can share its name (and slug) with the Portuguese one
      const g = bySlug[slug];
      const lang = g && baseLang(g.language);
      if (g && !used.has(slug) && !versions[lang]) { versions[lang] = g; used.add(slug); }
    });
    if (Object.keys(versions).length) groups.push(makeGroup(def.key, versions, def));
  });

  const auto = {};
  entries.filter(g => !used.has(g.slug)).forEach(g => {
    const fp = fingerprint(g);
    if (!auto[fp]) auto[fp] = {};
    let lang = baseLang(g.language);
    if (auto[fp][lang]) lang = `${lang}-${g.slug}`; // two guides in one language: keep both
    auto[fp][lang] = g;
  });
  Object.values(auto).forEach(versions => {
    const clean = {};
    Object.entries(versions).forEach(([l, g]) => {
      if (l.includes('-')) groups.push(makeGroup(g.slug, { [baseLang(g.language)]: g }));
      else clean[l] = g;
    });
    if (Object.keys(clean).length) {
      const first = clean.en || Object.values(clean)[0];
      groups.push(makeGroup(first.slug, clean));
    }
  });
  return groups;
};

// ─── HTML pieces ─────────────────────────────────────────────────────────────

const player = (sample, label, cls = '') => {
  const note = sample.lang === 'en' ? '' : ` (in ${langName(sample.lang)})`;
  const stop = String(sample.stop || '').split(':')[0].trim().replace(/\.$/, '');
  return playButton({ url: sample.url, label, small: `${stop}${note}`, aria: `Play a sample: ${stop}${note}` }, cls);
};

const card = (g, big = false) => {
  const langs = g.langs.length > 1
    ? g.langs.map(l => `<a href="${e(g.versions[l].url)}" lang="${e(l)}" title="${e(langName(l))} version">${e(l.toUpperCase())}</a>`).join('')
    : '';
  const only = g.primaryLang !== 'en' ? `<span class="lx-tag lx-tag-lang">In ${e(langName(g.primaryLang))}</span>` : '';
  const partner = g.partner ? `<a class="lx-tag lx-tag-partner" href="${e(g.partner)}">GuideSofia partner museum</a>` : '';
  const sample = g.sample ? player(g.sample, 'Play a sample', 'lx-play-sm') : '';
  const img = g.thumb
    ? `<img src="${e(g.thumb)}" alt="" loading="lazy" decoding="async" width="640" height="400">`
    : '';
  return `<article class="lx-card${big ? ' lx-card-big' : ''}" data-group="${e(g.theme)}" id="guide-${e(g.key)}">`
    + `<a class="lx-card-img" href="${e(g.primary.url)}" tabindex="-1" aria-hidden="true">${img}</a>`
    + `<div class="lx-card-body"><div class="lx-card-meta"><span>${g.stops} stops</span>${only}${partner}</div>`
    + `<h3><a href="${e(g.primary.url)}">${e(g.title)}</a></h3>${g.blurb ? `<p>${e(g.blurb)}</p>` : ''}${sample}`
    + `<div class="lx-card-foot"><span class="lx-langs" aria-label="Languages">${langs}</span><a class="lx-open" href="${e(g.primary.url)}">Open guide <span aria-hidden="true">→</span></a></div>`
    + `</div></article>`;
};

const head = (title, em) => `${e(title)}<em>${e(em)}</em>`;

/**
 * @param {Object} city   - from findCityPages: { key, city, country, countrySlug, citySlug, url }
 * @param {Array} entries - generated guides of this city: { slug, name, url, language, stops, firstStopName,
 *                          firstStop:{lat,lng}, thumbnail, description, sample }
 * @param {Object} content - optional content file (src/data/cityPages)
 * @param {Array} cityPages - all city pages (to link a day trip to another city)
 * @param {Object} research - optional planner research (public/city-research, from guide-studio/city_pages.py);
 *                            entries may then carry points:[{name,lat,lng}] so places can show our audio
 * @returns {{ html: string, stats: Object }}
 */
export function generateCityPage(city, entries, content = {}, cityPages = [], research = null) {
  const name = city.city;
  const groups = groupGuides(entries, content);
  const byKey = {};
  groups.forEach(g => { byKey[g.key] = g; });
  const N = groups.length;
  const nPages = groups.reduce((s, g) => s + g.langs.length, 0);
  const nStops = groups.reduce((s, g) => s + (g.stops || 0), 0);
  const langsUsed = [...new Set(groups.flatMap(g => g.langs))].sort((a, b) => langRank(a) - langRank(b));
  const pageUrl = SITE_BASE + city.url;
  // a country without guides has no country page yet: then the breadcrumb names it without a link
  const countryUrl = city.countryUrl === undefined ? `/guides/${city.countrySlug}/` : city.countryUrl;
  const updated = monthYear();
  const rs = research ? researchSections(name, research, groups) : null;
  const mode = N >= MANY_GUIDES || !rs ? 'many' : (N ? 'few' : 'none');
  const link = (k) => byKey[k] ? `<a href="${e(byKey[k].primary.url)}">${e(byKey[k].title)}</a>` : '';

  // themes: from the content file, plus "More guides" for anything it does not list
  const themes = [...(content.themes || [])];
  if (groups.some(g => g.theme === 'more')) themes.push(['more', content.themes ? 'More guides' : `All ${name} guides`]);
  const themed = themes.map(([k, t]) => [k, t, groups.filter(g => g.theme === k)]).filter(([, , list]) => list.length);

  // start here: from the content file, or the tours with the most languages and stops
  const startKeys = (content.startHere || []).filter(k => byKey[k]);
  const start = startKeys.length
    ? startKeys.map(k => byKey[k])
    : [...groups].sort((a, b) => (b.langs.length - a.langs.length) || (b.stops - a.stops) || (b.sample ? 1 : 0) - (a.sample ? 1 : 0)).slice(0, 3);

  const heroSample = (content.heroSample && byKey[content.heroSample] && byKey[content.heroSample].sample)
    ? byKey[content.heroSample] : (start.find(g => g.sample) || groups.find(g => g.sample));
  // 'guide:<key>' uses that guide's own photo, so a content file needs no image files of its own
  const photo = (ref) => {
    if (!ref) return '';
    if (ref.startsWith('guide:')) return (byKey[ref.slice(6)] && byKey[ref.slice(6)].thumb) || '';
    return ref;
  };
  const heroImage = photo(content.heroImage) || (start[0] && start[0].thumb) || (rs && rs.heroPhoto) || '';

  // ── sections ──
  const filters = themed.length > 1
    ? `<div class="lx-filters" role="toolbar" aria-label="Filter guides"><button type="button" class="is-on" data-filter="all">All guides <small>${N}</small></button>`
      + themed.map(([k, t, list]) => `<button type="button" data-filter="${e(k)}">${e(t)} <small>${list.length}</small></button>`).join('') + `</div>`
    : '';
  const grid = themed.map(([k, t, list]) =>
    (themed.length > 1 ? `<h3 class="lx-group-title" data-group="${e(k)}">${e(t)}</h3>` : '')
    + `<div class="lx-grid" data-group="${e(k)}">${list.map(g => card(g)).join('')}</div>`).join('');

  // areas: the content file's first (its text wins), then the planner's neighbourhoods; one with the same name
  // as a content-file area only adds its places to it
  const areaList = (content.areas || []).map(a => ({ ...a, places: [] }));
  ((rs && rs.neighbourhoods) || []).forEach(n => {
    const same = areaList.find(a => a.name.toLowerCase() === n.name.toLowerCase());
    if (same) same.places = n.places;
    else areaList.push({ name: n.name, text: n.text, guides: [], places: n.places });
  });
  const areas = areaList.map(a => {
    const items = (a.guides || []).filter(k => byKey[k]);
    if (!items.length && !a.places.length) return '';
    const img = photo(a.image);
    return `<div class="lx-area${img ? ' has-img' : ''}">${img ? `<img src="${e(img)}" alt="${e(a.name)}, ${e(name)}" loading="lazy" decoding="async">` : ''}`
      + `<div class="lx-area-body"><h3>${e(a.name)}</h3><p>${e(a.text)}</p>`
      + (items.length ? `<ul>${items.map(k => `<li>${link(k)}</li>`).join('')}</ul>` : '')
      + (a.places.length ? `<ul class="lx-area-places" aria-label="Places">${a.places.join('')}</ul>` : '')
      + `</div></div>`;
  }).join('');

  const timeline = (content.timeline || []).map(t =>
    `<li><span class="lx-year">${e(t.year)}</span><div><h3>${e(t.title)}</h3><p>${e(t.text)}</p>`
    + (byKey[t.guide] ? `<a class="lx-tl-link" href="${e(byKey[t.guide].primary.url)}">Hear it: ${e(byKey[t.guide].title)} →</a>` : '')
    + `</div></li>`).join('');

  const plan = content.plan;
  let planHtml = '';
  if (plan) {
    const days = (plan.days || []).map(d => {
      const stops = d.stops.filter(([k]) => byKey[k]);
      if (!stops.length) return '';
      return `<div class="lx-day"><span>${e(d.label)}</span><h3>${e(d.title)}</h3><ol>${stops.map(([k, n]) => `<li>${link(k)} <small>${e(n)}</small></li>`).join('')}</ol></div>`;
    }).join('');
    let dayTrip = '';
    if (plan.dayTrip) {
      const other = cityPages.find(c => c.key === plan.dayTrip.city);
      if (other) dayTrip = `<p class="lx-daytrip">${e(plan.dayTrip.text)} <a href="${e(other.url)}">${e(plan.dayTrip.link)} →</a></p>`;
    }
    const tips = (plan.tips || []).map(([h, p]) => `<div class="lx-tip"><h3>${e(h)}</h3><p>${e(p)}</p></div>`).join('');
    planHtml = `
  <section class="lx-sec lx-sec-dark" id="plan">
    <div class="lx-head"><p class="lx-kicker">Plan your visit</p><h2>${head(plan.title[0], plan.title[1])}</h2>${plan.intro ? `<p>${e(plan.intro)}</p>` : ''}</div>
    <div class="lx-days">${days}</div>
    ${dayTrip}
    ${tips ? `<div class="lx-tips">${tips}</div>` : ''}
  </section>`;
  }

  // FAQ: the planning questions from the research, then the standard audio questions with the real numbers
  // (only when the city has guides), plus the content file's own
  const langList = langsUsed.map(langName);
  const langSentence = langList.length > 1 ? `${langList.slice(0, -1).join(', ')} and ${langList[langList.length - 1]}` : langList[0];
  const startAnswer = content.startAnswer || (start.length ? `If it's your first visit, start with ${start[0].title}.` : '');
  const faqItems = [
    ...((rs && rs.faq) || []),
    ...(N ? [
      [`Is there an audio guide for ${name}?`, `Yes. GuideSofia has ${N} self-guided audio ${N === 1 ? 'tour' : 'tours'} for ${name}. You play them on your own phone in the GuideSofia app and walk at your own pace.`],
      [`Do the ${name} audio guides work offline?`, 'Yes. Download your itinerary and audio guides over Wi-Fi, then use them outdoors with GPS without spending mobile data.'],
      [`Which languages are the ${name} guides in?`, langsUsed.length > 1
        ? `The guides on this page are published in ${langSentence}. Each card shows which languages that guide has. In the app, the guide is narrated in 30+ languages.`
        : `The guides on this page are in ${langSentence}. In the app, the guide is narrated in 30+ languages.`],
      ['Is it free?', "The GuideSofia app is free to download, and it's free to start your first tour."],
      ...(startAnswer ? [[rs ? 'Which audio guide should I start with?' : 'Where should I start?', startAnswer]] : []),
    ] : []),
    ...(content.extraFaq || []),
    ...(N ? [['Can I ask the guide questions?', 'Yes. Open the chat in the app and ask about what you are looking at, local history, food or nearby places.']] : []),
  ];
  const faq = faqItems.map(([q, a], i) => `<details class="lx-faq-item"${i === 0 ? ' open' : ''}><summary>${e(q)}</summary><p>${e(a)}</p></details>`).join('');

  const partner = content.partnerBox;
  const b2bText = partner ? partner.text : "We turn the texts you already have into an audio guide on the visitor's own phone, approved by your team.";
  const b2bSecond = partner && partner.caseStudy
    ? `<a class="lx-btn-ghost" href="${e(partner.caseStudy.url)}">${e(partner.caseStudy.label)}</a>`
    : `<a class="lx-btn-ghost" href="https://www.guidesofia.com/business/">How it works</a>`;

  // ── sections, in the order that fits the city ──
  const startHtml = start.length ? `
  <section class="lx-sec" id="start">
    <div class="lx-head"><p class="lx-kicker">Start here</p><h2>${head(start.length === 1 ? 'One guide for a ' : `${start.length === 2 ? 'Two' : 'Three'} guides for a `, 'first visit')}</h2>
    <p>Not sure where to begin? ${start.length > 1 ? 'These cover' : 'This covers'} the city's best-known places.${start.some(g => g.sample) ? ' Press play to hear how the first stop sounds.' : ''}</p></div>
    <div class="lx-grid lx-grid-big">${start.map(g => card(g, true)).join('')}</div>
  </section>` : '';
  const guidesHtml = N ? `
  <section class="lx-sec lx-sec-paper" id="guides">
    <div class="lx-head"><p class="lx-kicker">All guides</p><h2>${head(`Every ${name} guide`, themed.length > 1 ? ', by theme' : '')}</h2>
    <p>${N} ${N === 1 ? 'guide' : 'guides'}${nPages > N ? ` and ${nPages} language versions. The letters on each card open the guide in that language` : ''}.</p></div>
    ${filters}
    ${grid}
  </section>` : '';
  // a few guides: one strip with all of them
  const storiesHtml = N ? `
  <section class="lx-sec lx-sec-paper" id="guides">
    <div class="lx-head"><p class="lx-kicker">Our audio guides</p><h2>${head('Hear the stories ', 'where they happened')}</h2>
    <p>${e((content.lead || `{n} self-guided audio ${N === 1 ? 'tour' : 'tours'} for ${name}. Play them on your phone, walk at your own pace, and ask the guide anything along the way.`).replace('{n}', N))}</p></div>
    <div class="lx-grid lx-grid-big">${groups.map(g => card(g, true)).join('')}</div>
  </section>` : '';
  // with research, the areas are a map: the content file's areas join it with their guides
  const mapHtml = rs ? rs.areaMap((content.areas || []).map(a => ({ name: a.name, text: a.text, groups: (a.guides || []).map(k => byKey[k]).filter(Boolean) }))) : '';
  const areasHtml = mapHtml || (areas ? `
  <section class="lx-sec" id="areas">
    <div class="lx-head"><p class="lx-kicker">By area</p><h2>${head(`${name}, `, 'neighbourhood by neighbourhood')}</h2>
    <p>${rs ? `The parts of town worth knowing and the places in each${N ? ', with the ones our guides cover marked' : ''}.` : 'Where each guide takes you, so you can plan by part of town.'}</p></div>
    <div class="lx-areas">${areas}</div>
  </section>` : '');
  const historyHtml = timeline ? `
  <section class="lx-sec" id="history">
    <div class="lx-head"><p class="lx-kicker">History</p><h2>${head(...(content.timelineTitle || [`${name} in `, 'a few moments']))}</h2>
    <p>The short version. Each moment links to the guide that tells it on the spot.</p></div>
    <ol class="lx-timeline">${timeline}</ol>
  </section>` : '';
  const r = rs || {};
  const planLabel = (plan && plan.navLabel) || (mode === 'many' ? 'Plan your visit' : 'With our guides');
  const order = {
    many: [['start', startHtml, 'Start here'], ['guides', guidesHtml, 'All guides'], ['see', r.see, 'What to see'], ['areas', areasHtml, rs && rs.areaMap && mapHtml ? 'Where to stay' : 'By area'],
      ['plan', planHtml, planLabel], ['when', r.when, 'When to go'],
      ['transport', r.transport, 'Getting around'], ['safety', r.safety, 'Safety'], ['history', historyHtml, 'History']],
    few: [['see', r.see, 'What to see'], ['guides', storiesHtml, 'Audio guides'], ['plan', planHtml, planLabel], ['areas', areasHtml, rs && rs.areaMap && mapHtml ? 'Where to stay' : 'By area'],
      ['when', r.when, 'When to go'], ['transport', r.transport, 'Getting around'],
      ['safety', r.safety, 'Safety'], ['history', historyHtml, 'History']],
    none: [['see', r.see, 'What to see'], ['areas', areasHtml, rs && rs.areaMap && mapHtml ? 'Where to stay' : 'By area'], ['when', r.when, 'When to go'],
      ['transport', r.transport, 'Getting around'], ['safety', r.safety, 'Safety'], ['history', historyHtml, 'History']],
  }[mode].filter(([, html]) => html);
  const body = order.map(([, html]) => html).join('');
  const jump = [...order.map(([id, , t]) => [id, t]), ['faq', 'FAQ']]
    .map(([id, t]) => `<a href="#${id}">${e(t)}</a>`).join('');

  // ── structured data ──
  const ld = [
    { '@context': 'https://schema.org', '@type': 'BreadcrumbList', itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Audio guides', item: `${SITE_BASE}/guides/` },
      { '@type': 'ListItem', position: 2, name: city.country, ...(countryUrl ? { item: SITE_BASE + countryUrl } : {}) },
      { '@type': 'ListItem', position: 3, name, item: pageUrl }] },
    ...(N ? [{ '@context': 'https://schema.org', '@type': 'CollectionPage', name: `${name} audio guides`, url: pageUrl, inLanguage: 'en',
      dateModified: new Date().toISOString().split('T')[0],
      about: { '@type': 'City', name, containedInPlace: { '@type': 'Country', name: city.country } },
      publisher: { '@type': 'Organization', name: 'GuideSofia', url: 'https://www.guidesofia.com/' },
      mainEntity: { '@type': 'ItemList', numberOfItems: N, itemListElement: groups.map((g, i) => ({
        '@type': 'ListItem', position: i + 1, item: { '@type': 'TouristTrip', name: g.title, url: SITE_BASE + g.primary.url, ...(g.blurb ? { description: g.blurb } : {}) } })) } }] : []),
    ...(rs ? [{ '@context': 'https://schema.org', '@type': 'TouristDestination', name, url: pageUrl,
      containedInPlace: { '@type': 'Country', name: city.country },
      includesAttraction: rs.topNames.map(n => ({ '@type': 'TouristAttraction', name: n })) }] : []),
    { '@context': 'https://schema.org', '@type': 'FAQPage', mainEntity: faqItems.map(([q, a]) => ({ '@type': 'Question', name: q, acceptedAnswer: { '@type': 'Answer', text: a } })) },
  ];
  const ldHtml = ld.map(x => `  <script type="application/ld+json">${JSON.stringify(x).replace(/</g, '\\u003c')}</script>`).join('\n');

  // a guide hub (many guides, or no research) keeps its audio-guide title; otherwise the page is the city's travel guide
  const hub = mode === 'many';
  const highlights = groups.slice(0, 5).map(g => g.title.split(':')[0]).join(', ');
  const title = hub
    ? `${name} Audio Guides: ${N} Self-Guided ${N === 1 ? 'Tour' : 'Tours'} | GuideSofia`
    : `${name} Travel Guide: What to See, Neighbourhoods & Tips | GuideSofia`;
  const description = hub
    ? `${N} self-guided audio ${N === 1 ? 'tour' : 'tours'} for ${name}: ${highlights} and more. Play a sample, then walk at your own pace. Works offline.`
    : `Plan ${name}: the places to see, the neighbourhoods, the best months, getting around and safety, checked against official sources.${N ? ` Plus ${N} audio ${N === 1 ? 'guide' : 'guides'} to hear the stories.` : ''}`;
  const ogImage = heroImage && !/^https?:/.test(heroImage) ? pageUrl + heroImage : heroImage;

  const st = r.stats || {};
  const lead = hub
    ? (content.lead || `{n} self-guided audio ${N === 1 ? 'tour' : 'tours'} for ${name}. Play them on your phone, walk at your own pace, and ask the guide anything along the way.`).replace('{n}', N)
    : `What to see in ${name}, the neighbourhoods, when to go, getting around and staying safe. Prices, hours and travel advice show their official source and the month we checked them.${N ? ` And ${N} audio ${N === 1 ? 'guide' : 'guides'} of ours, to hear the stories where they happened.` : ''}`;
  const heroStats = hub
    ? [['Guides', N], ['Stops', nStops], langsUsed.length > 1 ? ['Languages', langsUsed.length] : ['App languages', '30+'], ['Offline', 'Yes']]
    : [['Places', st.places], ...(st.best ? [['Best months', st.best]] : []),
      ...(st.days ? [['Ideal stay', `${st.days} days`]] : []), ...(N ? [['Audio guides', N]] : [])];

  const html = `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>${e(title)}</title>
  <meta name="description" content="${e(description)}">
  <link rel="canonical" href="${pageUrl}">
  <meta name="robots" content="index, follow, max-image-preview:large">
  <meta property="og:type" content="website">
  <meta property="og:site_name" content="GuideSofia">
  <meta property="og:url" content="${pageUrl}">
  <meta property="og:title" content="${e(title)}">
  <meta property="og:description" content="${e(description)}">
  ${ogImage ? `<meta property="og:image" content="${e(ogImage)}">` : ''}
  <meta name="twitter:card" content="summary_large_image">
  <meta name="apple-itunes-app" content="app-id=6748287909">
${ldHtml}
  <link rel="stylesheet" href="https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.0.0/css/all.min.css">
  <link rel="stylesheet" href="../../seo-styles.css">
  <link rel="stylesheet" href="../../city-page.css">
  <script async src="https://www.googletagmanager.com/gtag/js?id=${GOOGLE_TAG}"></script>
  <script>window.dataLayer=window.dataLayer||[];function gtag(){dataLayer.push(arguments);}gtag('js',new Date());gtag('config','${GOOGLE_TAG}');</script>
</head>
<body>
<div id="header-container"></div>
<main class="lx" id="main">
  <header class="lx-hero lx-hero-city">
    ${heroImage ? `<img class="lx-hero-bg" src="${e(heroImage)}" alt="" fetchpriority="high">` : ''}
    <div class="lx-hero-in">
      <nav class="lx-crumbs" aria-label="Breadcrumb"><a href="/guides/">Audio guides</a><span>/</span>${countryUrl ? `<a href="${e(countryUrl)}">${e(city.country)}</a>` : `<span>${e(city.country)}</span>`}<span>/</span><b aria-current="page">${e(name)}</b></nav>
      <p class="lx-eyebrow">${e(name)} · ${e(city.country)} · Updated ${e(updated)}</p>
      <h1>${e(hub ? `${name} audio guides` : `${name} travel guide`)}<em>${e(content.heroTagline || (hub ? 'Hear the city where it happened.' : 'Plan your days, then hear the stories.'))}</em></h1>
      <p class="lx-lead">${e(lead)}</p>
      <div class="lx-hero-actions">${heroSample ? player(heroSample.sample, `Hear a sample · ${heroSample.title}`, 'lx-play-hero') : ''}${stores('hero')}</div>
      <dl class="lx-stats">
        ${heroStats.map(([t, v]) => `<div><dt>${e(t)}</dt><dd${String(v).length > 9 ? ' class="lx-dd-long"' : ''}>${e(v)}</dd></div>`).join('\n        ')}
      </dl>
    </div>
  </header>

  <nav class="lx-jump" aria-label="On this page">${jump}</nav>
${body}

  <section class="lx-sec lx-sec-paper" id="faq">
    <div class="lx-head"><p class="lx-kicker">FAQ</p><h2>${head('Questions about ', hub ? `${name} audio guides` : name)}</h2></div>
    <div class="lx-faq">${faq}</div>
  </section>

  <section class="lx-cta">
    <div class="lx-cta-in">
      <h2>${head(`Take ${name} `, 'in your pocket')}</h2>
      <p>${N ? "Download GuideSofia, pick a guide and start walking. It's free to start your first tour." : 'GuideSofia audio guides play on your phone, offline, in 30+ languages. Free to download.'}</p>
      ${stores('cta', true)}
    </div>
  </section>

  <aside class="lx-b2b">
    <div><p class="lx-kicker">For museums &amp; cities</p>
    <h2>Run a museum or heritage site in ${e(name)}?</h2>
    <p>${e(b2bText)}</p></div>
    <div class="lx-b2b-links"><a class="lx-btn" href="https://www.guidesofia.com/business/#demo" data-gs-event="demo_click_city">Book a 15-minute demo</a>${b2bSecond}</div>
  </aside>

  <div class="lx-bar" aria-hidden="true"><span>${e(name)} · ${N ? `${N} audio ${N === 1 ? 'guide' : 'guides'}` : 'travel guide'}</span><a href="${IOS_URL}" data-store>Get the app</a></div>
</main>
<audio id="lx-audio" preload="none"></audio>
<div id="footer-container"></div>
<script src="../../city-page.js"></script>
<script src="../../seo-scripts.js"></script>
</body>
</html>
`;
  return { html, stats: { guides: N, pages: nPages, stops: nStops, languages: langsUsed, samples: groups.filter(g => g.sample).length,
    mode, research: !!rs, places: st.places || 0, audioPlaces: st.audioPlaces || 0 } };
}
